import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import './OrgasmeatOrderReceipt.css';

export const ORGASMEAT_DEMO_ID = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const isText = (value, max) => typeof value === 'string' && value.trim().length > 0 && value.length <= max;
const validTime = value => typeof value === 'string' && Number.isFinite(Date.parse(value));
const money = cents => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
const paymentLabels = { cb: 'Carte bancaire', espece: 'Espèces' };

// This public display accepts only the isolated demo contract, never an operator order.
export function validateOrgasmeatDemoOrder(value) {
  if (!value || !ORGASMEAT_DEMO_ID.test(value.id || '') || value.paymentStatus !== 'demo'
    || (value.paymentMethod !== undefined && !['cb', 'espece'].includes(value.paymentMethod))
    || !Number.isSafeInteger(value.ticketNumber) || value.ticketNumber < 1
    || !Number.isSafeInteger(value.totalCents) || value.totalCents < 0
    || !validTime(value.createdAt) || !validTime(value.expiresAt) || Date.parse(value.expiresAt) <= Date.parse(value.createdAt)
    || !Array.isArray(value.items) || value.items.length < 1 || value.items.length > 200
    || value.items.some(item => !item || !isText(item.name, 300) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 99
      || !Array.isArray(item.details) || item.details.length > 50 || item.details.some(detail => !isText(detail, 500)))) return null;
  return { id: value.id, ticketNumber: value.ticketNumber, createdAt: value.createdAt, expiresAt: value.expiresAt,
    totalCents: value.totalCents, paymentStatus: 'demo', ...(value.paymentMethod ? { paymentMethod: value.paymentMethod } : {}),
    items: value.items.map(item => ({ name: item.name, quantity: item.quantity, details: [...item.details] })) };
}

export function orgasmeatPickupToken(hash) {
  const match = /^#token=([A-Za-z0-9_-]{43})$/.exec(String(hash || ''));
  return match?.[1] || '';
}

export function orgasmeatPickupLink(value, id) {
  if (typeof value !== 'string' || !ORGASMEAT_DEMO_ID.test(id || '')) return '';
  try {
    const origin = window.location.origin;
    const url = new URL(value, origin);
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    const allowedOrigin = url.origin === origin || ['https://bekiosk.fr', 'https://www.bekiosk.fr'].includes(url.origin);
    if (!allowedOrigin || (url.protocol !== 'https:' && !(url.protocol === 'http:' && local && url.origin === origin))
      || url.username || url.password || url.search || url.pathname !== `/orgasmeat/retrait/${id}` || !orgasmeatPickupToken(url.hash)) return '';
    return url.href;
  } catch { return ''; }
}

export function OrgasmeatReceiptLines({ order }) {
  return <section className="om-receipt-ticket" aria-label="Détail du ticket de démonstration">
    <div className="om-receipt-ticket-head"><span>ORGASMEAT</span><small>Ticket cuisine · démonstration</small></div>
    <ol className="om-receipt-lines">{order.items.map((item, index) => <li key={`${index}-${item.name}`}>
      <span className="om-receipt-quantity">{item.quantity} ×</span>
      <div><strong>{item.name}</strong>{item.details.length > 0 && <ul>{item.details.map((detail, detailIndex) => <li key={detailIndex}>{detail}</li>)}</ul>}</div>
    </li>)}</ol>
    <div className="om-receipt-total"><span>Total TTC</span><strong>{money(order.totalCents)}</strong></div>
    {order.paymentMethod && <div className="om-receipt-payment" aria-label="Paiement de démonstration">
      <strong><span aria-hidden="true">✓ </span>Paiement accepté</strong><span>{paymentLabels[order.paymentMethod]}</span><small>Simulation · aucun paiement réel</small>
    </div>}
    <p className="om-receipt-ticket-note">Aucun paiement effectué. Aucun envoi en cuisine.</p>
  </section>;
}

export default function OrgasmeatOrderReceipt({ order: input, onRestart }) {
  const order = validateOrgasmeatDemoOrder(input);
  const link = order ? orgasmeatPickupLink(input.pickupUrl, order.id) : '';
  const [qr, setQr] = useState({ link: '', image: '', error: false });
  useEffect(() => {
    let active = true;
    setQr({ link, image: '', error: false });
    if (link) QRCode.toDataURL(link, { width: 512, margin: 4, errorCorrectionLevel: 'M', color: { dark: '#51182f', light: '#ffffff' } })
      .then(image => { if (active) setQr({ link, image, error: false }); })
      .catch(() => { if (active) setQr({ link, image: '', error: true }); });
    return () => { active = false; };
  }, [link]);
  return <section className="om-order-receipt" aria-label="Confirmation de ta commande de démonstration">
    <header className="om-receipt-brand"><img src="/orgasmeat/logo.svg" alt="ORGASMEAT" /><span>Démonstration</span></header>
    {order ? <>
      <div className="om-receipt-title"><span>{order.paymentMethod ? 'Paiement de démonstration' : 'Le plaisir est servi.'}</span><h1>{order.paymentMethod ? 'Paiement accepté' : 'C’est dans la boîte.'}</h1><p>Garde ton numéro. Il est aussi sur ton téléphone en scannant le QR.</p></div>
      <div className="om-receipt-layout">
        <section className="om-receipt-pickup" aria-label="Ton numéro de retrait">
          <span className="om-receipt-label">Ta commande</span><strong className="om-receipt-number">#{String(order.ticketNumber).padStart(4, '0')}</strong>
          {link ? <div className="om-receipt-qr">
            {qr.link === link && qr.image ? <img src={qr.image} width="256" height="256" alt={`QR pour conserver le ticket de démonstration ${order.ticketNumber}`} /> : <p role="status">{qr.link === link && qr.error ? 'Le QR est indisponible. Tu peux ouvrir ton ticket avec le lien ci-dessous.' : 'Préparation du QR…'}</p>}
            <a href={link} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Ouvrir mon ticket de retrait</a>
            <small>Scanne pour garder ton numéro et le détail de ta commande.</small>
          </div> : <p>Garde ce numéro. Le lien de retrait n’est pas disponible.</p>}
        </section>
        <OrgasmeatReceiptLines order={order} />
      </div>
    </> : <div className="om-receipt-title"><h1>Ticket indisponible</h1><p role="alert">Cette commande de démonstration ne peut pas être affichée.</p></div>}
    <footer className="om-receipt-footer"><p>Une expérience de démonstration, sans paiement ni préparation réelle.</p><button type="button" className="om-receipt-button" onClick={onRestart}>Terminer</button></footer>
  </section>;
}
